import { ColumnState } from 'ag-grid-community';
import { DateRange } from 'react-day-picker';
import { create } from 'zustand';

import { getCurrentMonthDateRange } from 'src/utils/date/dateUtils';
import { defaultTQC1040_R01Dto, TQC1040_R01Dto } from './Dto_TQC1040_R01';

export interface TQC1040_R01Filter {
	compCd: string;
	inspectDt: DateRange | undefined;
	equipmentType: string;
	equipmentCd: string;
}

export const defaultTQC1040_R01Filter: TQC1040_R01Filter = {
	compCd: '',
	inspectDt: getCurrentMonthDateRange(),
	equipmentType: '',
	equipmentCd: '',
};

export interface Store_TQC1040_R01 {
	// 검색
	TQC1040_R01Filter: TQC1040_R01Filter;
	TQC1040_R01PastFilter: TQC1040_R01Filter;
	setTQC1040_R01Filter: (filter: Partial<TQC1040_R01Filter>) => void;
	setTQC1040_R01PastFilter: (filter: Partial<TQC1040_R01Filter>) => void;

	// 목록
	TQC1040_R01List: TQC1040_R01Dto[];
	setTQC1040_R01List: (list: TQC1040_R01Dto[]) => void;
	updateTQC1040_R01List: (valueIndex: number, data: TQC1040_R01Dto) => void;

	// 원본 (변경 감지)
	originalTQC1040_R01List: TQC1040_R01Dto[];
	setOriginalTQC1040_R01List: (list: TQC1040_R01Dto[]) => void;

	// 선택 (행 하이라이트)
	selectedTQC1040_R01List: string[];
	setSelectedTQC1040_R01List: (idList: string[]) => void;
	addSelectedTQC1040_R01List: (idList: string[]) => void;
	removeSelectedTQC1040_R01List: (idList?: string[]) => void;
	initSelectedTQC1040_R01List: () => void;
	allSelectedTQC1040_R01List: () => void;

	// 컬럼 상태
	columnState: ColumnState[] | null;
	setColumnState: (state: ColumnState[] | null) => void;

	scrollTop: number | null;
	setScrollTop: (top: number | null) => void;

	allCount: number;
	setAllCount: (allCount: number) => void;

	initData: () => void;
}

/**
 * 서버에서 받은 목록에 그리드 식별/정렬용 인덱스(valueIndex·orderIndex·orderIndexSeq)를 1부터 부여한다.
 * 기본값 DTO 를 먼저 펼쳐 누락 필드를 채운 뒤 서버 값으로 덮어써, 그리드가 기대하는 형태를 보장한다.
 */
const withIndex = (list: TQC1040_R01Dto[]): TQC1040_R01Dto[] => {
	let idx = 1;
	return list.map((item) => {
		const row = {
			...defaultTQC1040_R01Dto,
			...item,
			valueIndex: item.valueIndex > 0 ? item.valueIndex : idx,
			orderIndex: item.orderIndex > -1 ? item.orderIndex : idx,
			orderIndexSeq: Math.max(item.orderIndexSeq ?? 0, 0),
		};
		idx++;
		return row;
	});
};

export const useTQC1040_R01Store = create<Store_TQC1040_R01>((set) => ({
	TQC1040_R01Filter: defaultTQC1040_R01Filter,
	TQC1040_R01PastFilter: defaultTQC1040_R01Filter,
	setTQC1040_R01Filter: (filter) =>
		set((state) => ({ TQC1040_R01Filter: { ...state.TQC1040_R01Filter, ...filter } })),
	setTQC1040_R01PastFilter: (filter) =>
		set((state) => ({ TQC1040_R01PastFilter: { ...state.TQC1040_R01PastFilter, ...filter } })),

	TQC1040_R01List: [],
	setTQC1040_R01List: (list) => set({ TQC1040_R01List: withIndex(list) }),
	updateTQC1040_R01List: (valueIndex, data) =>
		set((state) => ({
			TQC1040_R01List: state.TQC1040_R01List.map((item) =>
				item.valueIndex === valueIndex ? data : item,
			),
		})),

	originalTQC1040_R01List: [],
	setOriginalTQC1040_R01List: (list) => set({ originalTQC1040_R01List: withIndex(list) }),

	selectedTQC1040_R01List: [],
	setSelectedTQC1040_R01List: (idList) => set({ selectedTQC1040_R01List: idList }),
	addSelectedTQC1040_R01List: (idList) =>
		set((state) => ({
			selectedTQC1040_R01List: [...state.selectedTQC1040_R01List, ...idList],
		})),
	removeSelectedTQC1040_R01List: (idList?) =>
		set((state) => ({
			selectedTQC1040_R01List: state.selectedTQC1040_R01List.filter((v) =>
				idList ? !idList.includes(v) : false,
			),
		})),
	initSelectedTQC1040_R01List: () => set({ selectedTQC1040_R01List: [] }),
	allSelectedTQC1040_R01List: () =>
		set((state) => ({
			selectedTQC1040_R01List: state.TQC1040_R01List.map((item) => item.valueIndex.toString()),
		})),

	columnState: null,
	setColumnState: (state) => set({ columnState: state }),

	scrollTop: null,
	setScrollTop: (top) => set({ scrollTop: top }),

	allCount: 0,
	setAllCount: (allCount) => set({ allCount }),

	initData: () =>
		set({
			TQC1040_R01Filter: defaultTQC1040_R01Filter,
			TQC1040_R01PastFilter: defaultTQC1040_R01Filter,
			TQC1040_R01List: [],
			originalTQC1040_R01List: [],
			selectedTQC1040_R01List: [],
			columnState: null,
			scrollTop: null,
			allCount: 0,
		}),
}));

export default useTQC1040_R01Store;
